// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Helmet } from 'react-helmet';
import ProductBase from '../../components/ProductBase/ProductBase';
import FeatureSelect from '../../components/FeatureSelect/FeatureSelect';
import classNames from 'classnames/bind';
import { CommaFormat } from '../../../../elements/Price/Price';
import { Link } from 'react-router-dom';
import environment from '../../../../environment';

import style from './SamsungGalaxy.scss';
import coverImg from '../../../../assets/img/custom-products/samsung-galaxy/coverImg.png';
import featureStyle from '../../components/FeatureSelect/FeatureSelect.scss';
import s20cloudblue from '../../../../assets/img/custom-products/samsung-galaxy/s20/s20_cloudblue.png';
import s20cloudpink from '../../../../assets/img/custom-products/samsung-galaxy/s20/s20_cloudpink.png';
import s20cosmicgrey from '../../../../assets/img/custom-products/samsung-galaxy/s20/s20_cosmicgrey.png';
import s20pluscosmicblack from '../../../../assets/img/custom-products/samsung-galaxy/s20+/s20+_black.png';
import s20pluscloudblue from '../../../../assets/img/custom-products/samsung-galaxy/s20+/S20+_Cloudblue.png';
import s20pluscosmicgrey from '../../../../assets/img/custom-products/samsung-galaxy/s20+/S20+_COSMICGREY.png';
import s20ultracosmicgrey from '../../../../assets/img/custom-products/samsung-galaxy/ultra/ultra_cosmicblack.png';
import s20ultracosmicblack from '../../../../assets/img/custom-products/samsung-galaxy/ultra/ultra_cosmic grey.png';

import type { LocaleState } from '../../../locale/duck';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +location: Location;
};

type State = {
  color: string;
  capacity: string;
  image: string;
  samsungSelected: string;
};

type SamsungGalaxyType = {
  name: string;
  colors: string[];
  capacity: string[];
  images: { [key: string]: string };
  urls: { [key: string]: { [key: string]: any } };
  model?: string;
  modelLabel?: string;
  prices: { [key: string]: string };
  information: { [key: string]: any };
  title: { [key: string]: string };
  description: { [key: string]: string };
};

const samsungGalaxyPages = [
  { name: 'S20', url: '/custom-product/samsung-galaxy-s20' },
  { name: 'S20+', url: '/custom-product/samsung-galaxy-s20+' },
  { name: 'S20 Ultra', url: '/custom-product/samsung-galaxy-s20-ultra' },
];

const samsungGalaxies: { [key: string]: SamsungGalaxyType } = {
  'samsung-galaxy-s20': {
    name: 'Samsung Galaxy S20',
    colors: ['cosmic-gray', 'cloud-blue', 'cloud-pink'],
    capacity: ['128'],

    images: {
      'cosmic-gray': `${environment.baseUrl['US']}${s20cosmicgrey}`,
      'cloud-blue': `${environment.baseUrl['US']}${s20cloudblue}`,
      'cloud-pink': `${environment.baseUrl['US']}${s20cloudpink}`,
    },
    urls: {
      '128': {
        'cosmic-gray': 'https://www.bhphotovideo.com/c/product/1543089-REG/samsung_sm_g981uzaaxaa_galaxy_s20_sm_g981u_128gb.html',
        'cloud-blue': 'https://www.bhphotovideo.com/c/product/1543090-REG/samsung_sm_g981ulbaxaa_s20_sm_g981u_128gb_smartphone.html',
        'cloud-pink': 'https://www.bhphotovideo.com/c/product/1543091-REG/samsung_sm_g981uziaxaa_galaxy_s20_sm_g981u_128gb.html',
      }
    },
    prices: {
      '128': '999.99',
    },
    information: {
      detail: 'customProduct.samsung.detail.content.galaxy.s20',
    },
    title: {
      PE: 'customProduct.samsung.title.galaxy.s20.PE',
      CO: 'customProduct.samsung.title.galaxy.s20.CO',
      US: 'customProduct.samsung.title.galaxy.s20.US',
    },
    description: {
      PE: 'customProduct.samsung.description.galaxy.s20.PE',
      CO: 'customProduct.samsung.description.galaxy.s20.CO',
      US: 'customProduct.samsung.description.galaxy.s20.US',
    },
  },
  'samsung-galaxy-s20+': {
    name: 'Samsung Galaxy S20+',
    colors: ['cosmic-gray', 'cloud-blue', 'cosmic-black'],
    capacity: ['128', '512'],
    images: {
      'cosmic-gray': `${environment.baseUrl['US']}${s20pluscosmicgrey}`,
      'cloud-blue': `${environment.baseUrl['US']}${s20pluscloudblue}`,
      'cosmic-black': `${environment.baseUrl['US']}${s20pluscosmicblack}`,
    },
    urls: {
      '128': {
        'cosmic-gray': 'https://www.bhphotovideo.com/c/product/1543092-REG/samsung_sm_g986uzaaxaa_galaxy_s20_sm_g986u_128gb.html',
        'cloud-blue': 'https://www.bhphotovideo.com/c/product/1543093-REG/samsung_sm_g986ulbaxaa_galaxy_s20_sm_g986u_512gb.html',
        'cosmic-black': 'https://www.bhphotovideo.com/c/product/1543094-REG/samsung_sm_g986uzkaxaa_galaxy_s20_sm_g986u_128gb.html',
      },
      '512': {
        'cosmic-black': 'https://www.bhphotovideo.com/c/product/1543095-REG/samsung_sm_g986uzkexaa_galaxy_s10_sm_g986u_512gb.html',
      },
    },
    prices: {
      '128': '1199.99',
      '512': ' 1349.99',
    },
    information: {
      detail: 'customProduct.samsung.detail.content.galaxy.s20+',
    },
    title: {
      PE: 'customProduct.samsung.title.galaxy.s20+.PE',
      CO: 'customProduct.samsung.title.galaxy.s20+.CO',
      US: 'customProduct.samsung.title.galaxy.s20+.US',
    },
    description: {
      PE: 'customProduct.samsung.description.galaxy.s20+.PE',
      CO: 'customProduct.samsung.description.galaxy.s20+.CO',
      US: 'customProduct.samsung.description.galaxy.s20+.US',
    },
  },
  'samsung-galaxy-s20-ultra': {
    name: 'Samsung Galaxy S20 Ultra',
    colors: ['cosmic-gray', 'cosmic-black'],
    capacity: ['128', '512'],
    images: {
      'cosmic-gray': `${environment.baseUrl['US']}${s20ultracosmicblack}`,
      'cosmic-black': `${environment.baseUrl['US']}${s20ultracosmicgrey}`,
    },
    urls: {
      '128': {
        'cosmic-gray': 'https://www.bhphotovideo.com/c/product/1543096-REG/samsung_sm_g988uzaaxaa_galaxy_s20_ultra_sm_g988u.html',
        'cosmic-black': 'https://www.bhphotovideo.com/c/product/1543097-REG/samsung_sm_g988uzkaxaa_galaxy_s20_ultra_sm_g988u.html',
      },
      '512': {
        'cosmic-black': 'https://www.bhphotovideo.com/c/product/1543098-REG/samsung_sm_g988uzkexaa_galaxy_s20_sm_g988u_512gb.html',
      },
    },
    prices: {
      '128': '1399.99',
      '512': '1599.99',
    },
    information: {
      detail: 'customProduct.samsung.detail.content.galaxy.s20.ultra',
    },
    title: {
      PE: 'customProduct.samsung.title.galaxy.s20.ultra.PE',
      CO: 'customProduct.samsung.title.galaxy.s20.ultra.CO',
      US: 'customProduct.samsung.title.galaxy.s20.ultra.US',
    },
    description: {
      PE: 'customProduct.samsung.description.galaxy.s20.ultra.PE',
      CO: 'customProduct.samsung.description.galaxy.s20.ultra.CO',
      US: 'customProduct.samsung.description.galaxy.s20.ultra.US',
    },
  },
};

const messages = defineMessages({
  detailTitle: {
    id: 'customProduct.samsung.detail.title',
  },
  capFrom: {
    id: 'customProduct.iphone.capacity.from',
  },
});

export default class SamsungGalaxy extends Component<Props, State> {
  state = {
    color: '',
    capacity: '',
    image: '',
    samsungSelected: '',
  };
  samsung: SamsungGalaxyType;

  constructor(props: Props) {
    super(props);
    const pathname = props.location.pathname.split('/');
    this.samsung = samsungGalaxies[pathname[2]];

    if (pathname[2].includes('ultra')) {
      this.samsung = samsungGalaxies['samsung-galaxy-s20-ultra'];
    } else if (pathname[2].includes('s20+')) {
      this.samsung = samsungGalaxies['samsung-galaxy-s20+'];
    } else {
      this.samsung = samsungGalaxies['samsung-galaxy-s20'];
    }
  }

  componentDidMount() {
    this.setState({
      color: this.samsung.colors[0],
      capacity: this.samsung.capacity[0],
      image: this.samsung.images[Object.keys(this.samsung.images)[0]],
      samsungSelected: this.samsung.name.split('Samsung Galaxy ')[1],
    })
  }

  handleChangeColor = (value: string) => {
    this.setState({
      color: value,
      image: this.samsung.images[value]
    });
  };

  handleChangeSamsungGalaxy = (value: string) => (
    this.setState({
      samsungSelected: value,
    })
  );

  handleChangeCapacity = (value: string) => {
    let o: { [key: string]: string } = {
      capacity: value,
    };
    if (value === '512') {
      o.color = 'cosmic-black';
    }
    this.setState(o);
  };

  calculatePrice(capacity: string) {
    return this.samsung.prices[capacity];
  }

  buildUrl = () => {
    // TODO
  };

  buildInformation = () => {
    const { locale, localValues } = this.props;
    return [
      {
        title: `${this.context.intl.formatMessage(messages.detailTitle)} ${this.samsung.name}`,
        text: this.context.intl.formatMessage({ id: this.samsung.information.detail }),
      },
    ]
  };

  render() {
    const { image, capacity, color, samsungSelected } = this.state;
    const { locale } = this.props;
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const description = this.context.intl.formatMessage({ id: this.samsung.description[locale.domainCountry || 'PE'] });
    const title = this.context.intl.formatMessage({ id: this.samsung.title[locale.domainCountry || 'PE'] });
    const onlineStore = {
      id: 'ST07230440',
      logo: 'https://res.cloudinary.com/qempo/image/upload/v1551903482/static/stores/samsung.png',
      name: 'SAMSUNG',
      onlineStoreId: 15649,
      slug: 'samsung',
      type: 'online-store',
      url: 'https://shop.samsung.com/',
    };

    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:image', content: `${baseUrl}${coverImg}` },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
          { name: 'twitter:twitter:description', content: description },
        ]}>
          <title>{title}</title>
        </Helmet>
        <ProductBase name={this.samsung.name}
                     image={image}
                     price={this.calculatePrice(capacity)}
                     shipping="0.00"
                     store={{ image: onlineStore.logo, country: 'USA' }}
                     url={this.samsung.urls[capacity] ? this.samsung.urls[capacity][color] : ''}
                     color={color}
                     capacity={capacity}
                     onlineStore={onlineStore}
                     categoryId={3}
                     information={this.buildInformation()}>
          <section className={style.selectColor}>
            <FormattedMessage id="customProduct.samsung.color">{
              text => <p>{text}:</p>
            }</FormattedMessage>
            {/* color selector*/}
            {capacity !== '512' && <FeatureSelect>
              {this.samsung.colors.map((c: string, i: number) => (
                <article key={i} className={classNames({ [featureStyle.active]: c === color })}
                         onClick={() => this.handleChangeColor(c)}>
                  <div className={classNames(style.circle, style[c])}/>
                  <p>{c.replace('-', ' ')}</p>
                </article>))}
            </FeatureSelect>}
            {capacity === '512' && <FeatureSelect>
              <article className={featureStyle.active}>
                <div className={classNames(style.circle, style['cosmic-black'])}/>
                <p>Cosmic Black</p>
              </article>
            </FeatureSelect>}
          </section>
          <section className={style.selectCapacity}>
            <FormattedMessage id="customProduct.samsung.capacity">{
              text => <p>{text}:</p>
            }</FormattedMessage>
            {/* capacity selector */}
            <FeatureSelect>
              {this.samsung.capacity.map((c: string, i: number) => (
                <article key={i} className={classNames({ [featureStyle.active]: c === capacity })}
                         onClick={() => this.handleChangeCapacity(c)}>
                  <p className={style.capacity}>{`${c} GB`}</p>
                  <p className={style.price}>
                    {`${this.context.intl.formatMessage(messages.capFrom)} `} {`$ ${CommaFormat({ price: this.calculatePrice(c) })}`}
                  </p>
                </article>))}
            </FeatureSelect>
          </section>
          <section className={style.selectSize}>
            <FormattedMessage id="customProduct.samsung.model">{
              text => <p>{text}:</p>
            }</FormattedMessage>
            {/* size selector */}
            <FeatureSelect>
              {samsungGalaxyPages.map((o: { [key: string]: string }, i: number) => (
                <Link key={i}
                      to={`${o.url}-${locale.domainCountry === 'CO' ? 'colombia' : 'peru'}`}
                      className={classNames({ [featureStyle.active]: o.name === samsungSelected })}
                      onClick={() => this.handleChangeSamsungGalaxy(o.name)}>
                  <p>Samsung Galaxy</p>
                  <p className={style.size}>{o.name}</p>
                </Link>))}
            </FeatureSelect>
          </section>
        </ProductBase>
      </Fragment>
    )
  }
}

SamsungGalaxy.propTypes = {
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  location: PropTypes.object,
};

SamsungGalaxy.contextTypes = {
  intl: intlShape,
};




// WEBPACK FOOTER //
// ./src/modules/custom-products/Samsung/components/SamsungGalaxy.jsx